import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Music, Inbox, Megaphone, DollarSign, SlidersHorizontal, X, Send, Sparkles, Check, LogOut, Loader2, Trash2, Plus, ImageIcon, Globe, Palette, Users, UserPlus, Music2, Upload, Radio, CalendarCheck } from 'lucide-react';
import AddCustomerModal from '../components/AddCustomerModal';
import { useAuth } from '../context/AuthContext';
import { API, fileUrl, errText } from '../api';
import { PERRY } from '../mock';
import AudioPlayer from '../components/AudioPlayer';
import FileDrop from '../components/FileDrop';
import { songLabel } from '../lib/song';
import Subscribers from '../components/studio/Subscribers';
import ContentQueue from '../components/studio/ContentQueue';
import ReleaseControls from '../components/studio/ReleaseControls';

const STATUS = {
  new: { label: 'New', cls: 'bg-gold text-ink' },
  studio: { label: 'In the studio', cls: 'border border-gold/40 text-gold' },
  ready: { label: 'Ready to listen', cls: 'border border-gold/40 text-gold' },
  purchased: { label: 'Purchased', cls: 'border border-emerald-500/40 text-emerald-400' },
};

export default function Studio() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const [view, setView] = useState('requests');
  const [addCustomer, setAddCustomer] = useState(null); // null | 'new' | customer object
  const [active, setActive] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [stats, setStats] = useState({ new: 0, inStudio: 0, waiting: 0, earned: 0 });
  const [marketing, setMarketing] = useState([]);
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(() => Promise.all([API.listRequests(), API.stats(), API.listMarketing(), API.payments(), API.plans()])
    .then(([r, s, m, p, pl]) => { setReqs(r); setStats(s); setMarketing(m); setPayments(p); setPlans(pl); })
    .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load the studio.')), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav('/studio/signin'); return; }
    if (user.role !== 'studio') { nav('/dashboard'); return; }
    load().finally(() => setReady(true));
  }, [user, loading, nav, load]);

  if (loading || !ready) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="text-gold animate-spin" size={28} /></div>;

  const NAV = [
    { id: 'requests', label: 'Requests', icon: Inbox, badge: stats.new },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'marketing', label: 'Marketing', icon: Megaphone, badge: marketing.filter((m) => m.status === 'new').length },
    { id: 'subscribers', label: 'Subscribers', icon: Radio },
    { id: 'content', label: 'Content', icon: CalendarCheck },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'settings', label: 'Settings', icon: SlidersHorizontal },
  ];

  const onDeleted = (code) => { setReqs((p) => p.filter((r) => r.code !== code)); setActive(null); API.stats().then(setStats); };
  const onSent = (updated, keepOpen = false) => { setReqs((p) => p.map((r) => r.code === updated.code ? updated : r)); if (keepOpen) setActive(updated); else setActive(null); API.stats().then(setStats); };

  return (
    <div className="min-h-screen bg-ink grain flex">
      <aside className="w-64 shrink-0 border-r hairline min-h-screen sticky top-0 hidden md:flex flex-col justify-between p-5">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-10 px-2">
            <span className="grid place-items-center w-10 h-10 rounded-full border hairline"><Music size={16} className="text-gold" /></span>
            <span className="font-display text-lg text-cream leading-none">Brand <span className="italic text-gold-grad">Melody</span><span className="block eyebrow text-[8px] mt-1">Studio</span></span>
          </Link>
          <nav className="space-y-1">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => setView(n.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${view === n.id ? 'bg-gold/10 text-cream border border-gold/20' : 'text-cream/60 hover:text-cream hover:bg-white/[0.03]'}`}>
                <n.icon size={17} className={view === n.id ? 'text-gold' : ''} />
                <span className="flex-1 text-left">{n.label}</span>
                {n.badge ? <span className="grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-heart text-cream text-[10px] font-bold">{n.badge}</span> : null}
              </button>
            ))}
          </nav>
        </div>
        <div>
          <button onClick={() => { logout(); nav('/'); }} className="w-full flex items-center gap-2 px-4 py-2.5 mb-3 rounded-xl text-sm text-cream/60 hover:text-cream hover:bg-white/[0.03] transition-colors"><LogOut size={15} /> Sign out</button>
          <div className="flex items-center gap-3 px-2">
            <img src={PERRY.avatar} alt="" className="w-10 h-10 rounded-full object-cover ring-1 ring-gold/30" />
            <div className="min-w-0"><p className="text-sm text-cream truncate">{user.owner_name || PERRY.name}</p><p className="text-[11px] text-cream/45 truncate">{PERRY.handle}</p></div>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-6 lg:px-12 py-12">
        {view === 'requests' && <Requests reqs={reqs} stats={stats} onOpen={setActive} onAdd={() => setAddCustomer('new')} />}
        {view === 'customers' && <Customers onAdd={() => setAddCustomer('new')} onNewSong={(c) => setAddCustomer(c)} reqs={reqs} />}
        {view === 'marketing' && <Marketing items={marketing} reload={load} />}
        {view === 'subscribers' && <Subscribers plans={plans} />}
        {view === 'content' && <ContentQueue />}
        {view === 'payments' && <Payments payments={payments} />}
        {view === 'settings' && <Settings />}
      </main>

      {active && <RequestPanel req={active} onClose={() => setActive(null)} onSent={onSent} onDeleted={onDeleted} />}
      {addCustomer && <AddCustomerModal customer={addCustomer === 'new' ? null : addCustomer} onClose={() => setAddCustomer(null)} onCreated={(res) => { setAddCustomer(null); load(); setActive(res.request); setView('requests'); }} />}
    </div>
  );
}

function Requests({ reqs, stats, onOpen, onAdd }) {
  return (
    <div>
      <p className="eyebrow mb-3">Studio</p>
      <h1 className="font-display text-5xl md:text-6xl text-cream leading-[0.95]">Good to see you, <span className="italic text-gold-grad">Perry.</span></h1>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
        <p className="text-cream/60">{stats.new} new request{stats.new === 1 ? '' : 's'} waiting on you.</p>
        <button onClick={onAdd} data-testid="add-customer-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><UserPlus size={15} /> Add customer</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        {[['New requests', stats.new], ['In the studio', stats.inStudio], ['Waiting on the client', stats.waiting]].map(([l, v]) => (
          <div key={l} className="panel rounded-2xl p-5"><p className="text-sm text-cream/55">{l}</p><p className="font-display text-4xl text-cream mt-2">{v}</p></div>
        ))}
        <div className="panel rounded-2xl p-5 border-gold/30"><p className="text-sm text-cream/55">Earned</p><p className="font-display text-4xl text-gold-grad mt-2">${stats.earned}</p></div>
      </div>

      <div className="mt-8 panel rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b hairline eyebrow text-[9px]">
          <span className="col-span-4">Business</span><span className="col-span-2">Requested</span><span className="col-span-2">Style</span><span className="col-span-2">Status</span><span className="col-span-2 text-right">Price</span>
        </div>
        {reqs.length === 0 && <p className="px-6 py-10 text-center text-sm text-cream/45" data-testid="requests-empty">No requests yet. Add a customer to start a song for them, or wait for one to come in from the site.</p>}
        {reqs.map((r) => (
          <button key={r.code} data-testid={`request-row-${r.code}`} onClick={() => onOpen(r)} className="w-full grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b hairline last:border-0 items-center text-left hover:bg-white/[0.02] transition-colors">
            <div className="col-span-2 md:col-span-4 flex items-center gap-3 min-w-0">
              <img src={r.avatar} alt="" className="w-10 h-10 rounded-lg object-cover ring-1 ring-gold/20 shrink-0" />
              <div className="min-w-0"><p className="text-cream font-medium truncate">{r.business}</p><p className="text-xs text-cream/45 truncate flex items-center gap-1.5">{r.owner} &middot; {r.type} {r.ai && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-gold/15 text-gold">AI</span>}</p></div>
            </div>
            <span className="hidden md:block col-span-2 text-sm text-cream/60">{r.requested}</span>
            <span className="hidden md:block col-span-2 text-sm text-cream/60">{r.style}</span>
            <span className="col-span-1 md:col-span-2"><span className={`inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 ${STATUS[r.status].cls}`}>{r.status !== 'new' && <span className="w-1.5 h-1.5 rounded-full bg-current" />}{STATUS[r.status].label}</span></span>
            <span className="col-span-1 md:col-span-2 text-right font-display text-lg text-cream">{r.price ? `$${r.price}` : '\u2014'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Customers({ onAdd, onNewSong, reqs }) {
  const [list, setList] = useState(null);
  useEffect(() => { API.customers().then(setList).catch(() => setList([])); }, [reqs]);
  return (
    <div>
      <p className="eyebrow mb-3">Studio</p>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-5xl text-cream leading-[0.95]">Customers.</h1>
        <button onClick={onAdd} data-testid="customers-add-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-2"><UserPlus size={15} /> Add customer</button>
      </div>
      <p className="mt-3 text-cream/60 max-w-xl">Create an account for a client who only has a phone. They get an email with their username and a temporary password, and the song you make lands in their dashboard.</p>
      <div className="mt-8 panel rounded-2xl overflow-hidden" data-testid="customers-list">
        {list === null && <div className="p-10 grid place-items-center"><Loader2 className="text-gold animate-spin" size={22} /></div>}
        {list && list.length === 0 && <p className="px-6 py-10 text-center text-sm text-cream/45">No customers yet.</p>}
        {list && list.map((c) => (
          <div key={c.email} data-testid={`customer-row-${c.email}`} className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4 border-b hairline last:border-0">
            <img src={c.avatar} alt="" className="w-10 h-10 rounded-lg object-cover ring-1 ring-gold/20 shrink-0 hidden sm:block" />
            <div className="min-w-0 flex-1">
              <p className="text-cream font-medium truncate">{c.business_name}</p>
              <p className="text-xs text-cream/45 truncate">{c.owner_name} &middot; {c.email}{c.phone ? ` \u00b7 ${c.phone}` : ''}</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-cream/50">
              <span>{c.songs} song{c.songs === 1 ? '' : 's'}</span>
              {c.created_by === 'studio' && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-gold/15 text-gold">BY PERRY</span>}
              {c.temp_password && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded border hairline">TEMP PW</span>}
            </div>
            <button onClick={() => onNewSong(c)} data-testid={`customer-new-song-${c.email}`} className="btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2"><Music2 size={13} className="text-gold" /> New song</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RequestPanel({ req, onClose, onSent, onDeleted }) {
  const [price, setPrice] = useState(req.price || '');
  const [preview, setPreview] = useState(req.preview_seconds ?? 90);
  const [msg, setMsg] = useState(req.message || '');
  const [versions, setVersions] = useState(req.versions || []);
  const [newTitle, setNewTitle] = useState('');
  const [launch, setLaunch] = useState(null);
  useEffect(() => { if (req.status === 'purchased') API.launch(req.code).then(setLaunch).catch(() => {}); }, [req.code, req.status]);
  const [busy, setBusy] = useState(false);
  const [kitBusy, setKitBusy] = useState(false);
  const files = req.profile_full?.files || [];
  const canSend = versions.length > 0 && versions.every((v) => v.audio && v.title.trim()) && Number(price) > 0;

  const getKit = async () => {
    setKitBusy(true);
    try { await API.downloadLogoKit(req.code, req.business); toast.success('Logo kit downloaded.'); }
    catch (e) { toast.error('Could not build the logo kit.'); }
    finally { setKitBusy(false); }
  };
  const setPreviewNow = async (v) => {
    setPreview(v);
    try {
      const updated = await API.patchRequest(req.code, { preview_seconds: v });
      onSent(updated, true);
      toast.success(v ? `Customer preview set to ${v} seconds.` : 'Customer hears the full song.', { description: req.status === 'ready' || req.status === 'purchased' ? 'Already live on their dashboard.' : 'Applies when you send the song.' });
    } catch (e) { toast.error('Could not update the preview length.'); }
  };
  const toggleShowcase = async () => {
    setBusy(true);
    try {
      const updated = await API.patchRequest(req.code, { showcase: !req.showcase });
      toast.success(updated.showcase ? 'Added to the public showcase.' : 'Removed from the showcase.');
      onSent(updated, true);
    } catch (e) { toast.error('Could not update showcase.'); }
    finally { setBusy(false); }
  };

  const owned = req.purchased_versions || [];
  const remove = async () => {
    const ok = window.confirm(`Delete the request for ${req.business} (${req.code})?\n\nIt will disappear from your Studio and from the customer's dashboard. This can't be undone.`);
    if (!ok) return;
    setBusy(true);
    try { await API.deleteRequest(req.code); toast.success(`Deleted ${req.business}.`); onDeleted(req.code); }
    catch (e) { toast.error(errText(e, 'Could not delete that request.')); setBusy(false); }
  };
  const nextId = () => { const used = new Set(versions.map((v) => v.id)); return 'ABCDEFGHIJ'.split('').find((l) => !used.has(l)) || 'Z'; };
  const addVersion = (res, file) => {
    const title = newTitle.trim() || req.business;
    setVersions((vs) => { const used = new Set(vs.map((v) => v.id)); const id = 'ABCDEFGHIJ'.split('').find((l) => !used.has(l)) || 'Z'; return [...vs, { id, title, genre: req.style, audio: res.url, file_id: res.id, file_name: file.name }]; });
    setNewTitle('');
  };
  const updateVersion = (i, patch) => setVersions((vs) => vs.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const removeVersion = async (i) => {
    const gone = versions[i];
    const next = versions.filter((_, j) => j !== i);
    setBusy(true);
    try {
      const updated = await API.patchRequest(req.code, { versions: next });
      setVersions(next);
      onSent(updated, true);
      toast.success(`Removed Take ${gone.id}.`, { description: next.length ? 'It\u2019s gone from the customer\u2019s dashboard.' : 'The customer no longer sees any versions; status is back to "in the studio".' });
    } catch (e) { toast.error(errText(e, 'Could not remove that version.')); }
    finally { setBusy(false); }
  };

  const save = async (status) => {
    setBusy(true);
    try {
      const updated = await API.patchRequest(req.code, { price: Number(price) || 0, preview_seconds: Number(preview), message: msg, status, versions });
      if (status === 'ready') toast.success(`Sent to ${req.business}.`, { description: 'They\u2019ll get an email that their song is ready to hear.' });
      else toast.success('Saved.', { description: status === 'studio' ? 'Marked as in the studio.' : 'Your changes are saved.' });
      onSent(updated);
    } catch (e) { toast.error('Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-panel border-l hairline h-full overflow-y-auto animate-[slidein_.4s_cubic-bezier(.16,1,.3,1)]" data-testid="request-panel">
        <style>{`@keyframes slidein{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>
        <div className="sticky top-0 bg-panel/95 backdrop-blur-xl border-b hairline px-7 py-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <img src={fileUrl(files.find((f) => f.kind === 'logo')?.url) || req.avatar} alt="" className="w-11 h-11 rounded-lg object-cover ring-1 ring-gold/20" />
            <div><p className="font-display text-xl text-cream leading-tight">{req.business}</p><p className="text-xs text-cream/45">{req.owner} &middot; {req.code}</p></div>
          </div>
          <button onClick={onClose} data-testid="request-panel-close" className="grid place-items-center w-9 h-9 rounded-full border hairline text-cream/60 hover:text-cream transition-colors"><X size={16} /></button>
        </div>

        <div className="px-7 py-6 space-y-7">
          <div>
            <p className="eyebrow text-[9px] mb-2">Profile</p>
            <p className="text-cream/80 text-sm leading-relaxed">{req.profile}</p>
            <p className="text-sm text-cream/55 mt-2">Style asked for: <span className="text-cream">{req.style}</span></p>
            {req.prefs?.length && <p className="text-sm text-cream/55">Length: <span className="text-cream">{req.prefs.length}</span>{req.prefs.energy && <> &middot; Energy: <span className="text-cream">{req.prefs.energy}</span></>}</p>}
            {req.prefs?.words && <p className="text-sm text-cream/55">Must include: <span className="text-cream">{req.prefs.words}</span></p>}
            {req.prefs?.notes && <p className="text-sm text-cream/55 mt-1">&ldquo;{req.prefs.notes}&rdquo;</p>}
          </div>

          {files.length > 0 && (
            <div>
              <p className="eyebrow text-[9px] mb-3 flex items-center gap-2"><ImageIcon size={12} className="text-gold" /> Logo and photos</p>
              <div className="flex flex-wrap gap-2">
                {files.map((f) => <a key={f.id} href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="relative block"><img src={fileUrl(f.url)} alt={f.name} className="w-20 h-20 rounded-xl object-cover ring-1 ring-gold/20 hover:ring-gold/60 transition-shadow" />{f.kind === 'logo' && <span className="absolute bottom-1 left-1 text-[8px] font-mono tracking-widest bg-gold text-ink rounded px-1">LOGO</span>}</a>)}
              </div>
              <button onClick={getKit} disabled={kitBusy} data-testid="studio-logo-kit-btn" className="mt-3 btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50">{kitBusy ? <Loader2 size={13} className="animate-spin" /> : <Palette size={13} className="text-gold" />} Download social logo kit</button>
            </div>
          )}

          {req.ai && req.brief && (
            <div className="rounded-2xl border border-gold/20 bg-gold/[0.03] p-5">
              <p className="flex items-center gap-2 eyebrow text-[9px] mb-4"><Sparkles size={13} className="text-gold" /> Song brief &middot; AI generated &middot; starting points only</p>
              <Brief label="Facts worth singing" items={req.brief.facts} />
              <Brief label="Hook ideas" items={req.brief.hooks} />
              <Brief label="Rhymes for the name" items={req.brief.rhymes} />
              <p className="text-sm text-cream/55 mt-3"><span className="text-cream/80">Sound:</span> {req.brief.sound}</p>
            </div>
          )}

          <div>
            <p className="eyebrow text-[9px] mb-3">Your versions {versions.length > 0 && <span className="text-gold">&middot; {versions.length} uploaded</span>}</p>
            <div className="space-y-3">
              {versions.map((v, i) => (
                <div key={v.id} className="rounded-xl border hairline p-4 space-y-3" data-testid={`version-row-${v.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-display text-lg text-cream">Take {v.id} {owned.includes(v.id) && <span className="ml-2 text-[10px] font-sans tracking-widest uppercase text-emerald-400 border border-emerald-500/40 rounded-full px-2 py-0.5 align-middle">Bought</span>}</p>
                    {!owned.includes(v.id) && <button onClick={() => removeVersion(i)} disabled={busy} data-testid={`version-remove-${v.id}`} className="flex items-center gap-1.5 text-xs text-cream/45 hover:text-heart transition-colors disabled:opacity-50"><Trash2 size={14} /> Delete</button>}
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-semibold text-gold mb-2" htmlFor={`version-title-${v.id}`}><Music2 size={13} /> Song name for Take {v.id}</label>
                    <input id={`version-title-${v.id}`} value={v.title} onChange={(e) => updateVersion(i, { title: e.target.value })} data-testid={`version-title-${v.id}`} placeholder="Type the song name here" className="w-full rounded-xl border border-gold/40 bg-ink px-4 py-3 text-base text-cream placeholder:text-cream/40 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors" />
                    <p className="text-[11px] text-cream/45 mt-1.5" data-testid={`version-label-${v.id}`}>Customer sees &amp; downloads: <span className="text-cream/70">{v.title.trim() ? songLabel(v) : `… - Take ${v.id}`}</span></p>
                  </div>
                  <AudioPlayer id={`${req.code}-${v.id}-studio`} src={v.audio} compact />
                  {v.file_name && <p className="text-[11px] text-cream/40 font-mono truncate">{v.file_name}</p>}
                </div>
              ))}
              <div className="rounded-2xl border border-gold/30 bg-gold/[0.03] p-4 space-y-3" data-testid="new-take-box">
                <p className="font-display text-2xl text-cream">Add Take {nextId()}</p>
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gold mb-2" htmlFor="new-take-title"><Music2 size={15} /> Step 1 &middot; Type the song name here</label>
                  <input id="new-take-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} data-testid="new-take-title" placeholder="Song name  (e.g. Welcome To SpinaWorks)" className="w-full rounded-xl border-2 border-gold/60 bg-ink px-4 py-3.5 text-lg text-cream placeholder:text-cream/40 outline-none focus:border-gold focus:ring-2 focus:ring-gold/30 transition-colors" />
                  <p className="text-xs text-cream/55 mt-2">Customer will see &amp; download: <span className="text-cream">{newTitle.trim() ? `${newTitle.trim()} - Take ${nextId()}` : `… - Take ${nextId()}`}</span></p>
                </div>
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-gold mb-2"><Upload size={15} /> Step 2 &middot; Upload the audio file</p>
                  <FileDrop accept="audio/*" onUploaded={addVersion} testId="version-upload">
                    <span className="flex items-center gap-2"><Plus size={14} className="text-gold" /> {newTitle.trim() ? `Drop or click to upload "${newTitle.trim()}" (MP3, WAV, M4A)` : `Drop or click to upload the file for Take ${nextId()} (MP3, WAV, M4A)`}</span>
                  </FileDrop>
                </div>
              </div>
              {versions.some((v, i) => (req.versions || [])[i]?.title !== v.title || (req.versions || []).length !== versions.length) && <p className="text-xs text-gold/80" data-testid="unsaved-note">Unsaved changes &mdash; titles and new uploads reach the customer when you click {req.status === 'purchased' || req.status === 'ready' ? '"Save changes"' : '"Send to client"'} below.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><label className="eyebrow text-[9px] block mb-2">Price for this song ($)</label><input value={price} onChange={(e) => setPrice(e.target.value)} data-testid="price-input" placeholder="750" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /></div>
            <div>
              <label className="eyebrow text-[9px] block mb-2">Customer preview {req.versions?.length > 0 && <span className="text-gold/70 normal-case tracking-normal font-sans">&middot; applies instantly</span>}</label>
              <div className="flex gap-1.5">
                {[[90, '90 sec'], [120, '120 sec'], [0, 'Full song']].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setPreviewNow(v)} data-testid={`preview-${v || 'full'}`} className={`flex-1 rounded-xl border px-2 py-3 text-xs transition-colors ${Number(preview) === v ? 'border-gold/60 text-gold bg-gold/[0.06]' : 'hairline text-cream/70 hover:border-gold/40'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="eyebrow text-[9px] block mb-2">Message to the client <span className="normal-case tracking-normal font-sans text-cream/40">&middot; paragraphs and **bold** are kept</span></label>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={8} placeholder="Hi Steve,&#10;&#10;Three takes on your song..." data-testid="message-input" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream placeholder:text-cream/35 outline-none focus:border-gold/50 transition-colors resize-y leading-relaxed" />
          </div>

          {req.status === 'purchased' ? (
            <div className="space-y-4" data-testid="purchased-tools">
              <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid="studio-purchased-summary"><Check size={16} /> Purchased &middot; {(req.purchased_versions || []).length > 1 ? `versions ${req.purchased_versions.join(', ')} · featured ${req.purchased_version}` : `version ${req.purchased_version}`}</p>
              {req.status === 'purchased' && <ReleaseControls code={req.code} />}
              {launch && (
                <div className="rounded-xl border hairline p-3 text-xs" data-testid="studio-launch-pad">
                  <p className="text-cream flex items-center gap-2">Social Launch Pad &middot; <span className={launch.unlocked ? 'text-emerald-400' : 'text-cream/45'}>{launch.unlocked ? `Unlocked · ${launch.level === 'pro' ? 'Pro' : 'Basic'}` : 'Locked until the customer buys a Launch Pad'}</span></p>
                  {launch.videos?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {launch.videos.map((v) => (
                        <li key={v.id} className="text-cream/65 flex items-center gap-2 flex-wrap">{v.title} &middot; <span className={v.status === 'ready' ? 'text-emerald-400' : v.status === 'failed' ? 'text-heart' : 'text-gold'}>{v.status}</span>
                          {Object.entries(v.formats || {}).map(([fmt, f]) => <a key={fmt} href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="text-gold hover:underline">{fmt}</a>)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <div className="rounded-xl border hairline p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-cream flex items-center gap-2"><Globe size={14} className="text-gold" /> Showcase on the public site</p>
                  <p className="text-xs text-cream/50 mt-1">Adds this song to the &ldquo;Listen&rdquo; section and makes its share page public.</p>
                </div>
                <button onClick={toggleShowcase} disabled={busy} data-testid="showcase-toggle" role="switch" aria-checked={!!req.showcase} className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${req.showcase ? 'bg-gold' : 'bg-white/10'}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink transition-transform ${req.showcase ? 'left-[22px]' : 'left-0.5'}`} /></button>
              </div>
              {(req.showcase || req.share_enabled) && <a href={`/song/${req.code}`} target="_blank" rel="noreferrer" data-testid="studio-share-link" className="text-xs text-gold hover:underline">Open song page &rarr;</a>}
              <button onClick={() => save('purchased')} disabled={busy || !versions.every((v) => v.title.trim())} data-testid="save-changes-btn" className="w-full btn-ghost rounded-full py-3 text-sm disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Save changes'}</button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button onClick={() => save('ready')} disabled={busy || !canSend} data-testid="send-to-client-btn" className="w-full btn-gold rounded-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> {req.status === 'ready' ? 'Save changes & notify client' : 'Send to client'}</>}</button>
              {!canSend && <p className="text-xs text-cream/45 text-center">Upload at least one version, give each a title and set a price to send.</p>}
              {req.status === 'new' && <button onClick={() => save('studio')} disabled={busy} data-testid="mark-studio-btn" className="w-full btn-ghost rounded-full py-3 text-sm">Mark as in the studio</button>}
              {req.status !== 'new' && <button onClick={() => save(req.status)} disabled={busy} data-testid="save-draft-btn" className="w-full btn-ghost rounded-full py-3 text-sm">{req.status === 'ready' ? 'Save changes' : 'Save without sending'}</button>}
            </div>
          )}
          <button onClick={remove} disabled={busy} data-testid="delete-request-btn" className="w-full mt-4 flex items-center justify-center gap-2 text-xs text-cream/40 hover:text-heart transition-colors disabled:opacity-50"><Trash2 size={13} /> Delete this request{req.status === 'purchased' ? ' (customer keeps their purchased songs)' : ''}</button>
        </div>
      </div>
    </div>
  );
}

function Brief({ label, items }) {
  return (
    <div className="mb-3">
      <p className="text-[11px] text-cream/45 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-2">{(items || []).map((t) => <span key={t} className="text-sm text-cream/85 rounded-lg bg-panel2/60 border hairline px-2.5 py-1">{t}</span>)}</div>
    </div>
  );
}

function Marketing({ items, reload }) {
  const [vals, setVals] = useState({});
  const quote = async (code) => {
    try { await API.quoteMarketing(code, Number(vals[code]) || 400); toast.success('Quote sent.'); reload(); }
    catch (e) { toast.error('Could not send quote.'); }
  };
  return (
    <div>
      <p className="eyebrow mb-3">Studio</p>
      <h1 className="font-display text-5xl text-cream leading-[0.95]">Marketing <span className="italic text-gold-grad">requests.</span></h1>
      <div className="mt-8 space-y-4 max-w-3xl">
        {items.length === 0 && <p className="text-cream/50">No marketing requests yet.</p>}
        {items.map((m) => (
          <div key={m.code} className="panel rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <img src={m.avatar} alt="" className="w-11 h-11 rounded-lg object-cover ring-1 ring-gold/20" />
              <div className="flex-1"><p className="text-cream font-medium">{m.business}</p><p className="text-xs text-cream/45">{m.owner} &middot; {m.code}</p></div>
              <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 ${m.status === 'paid' ? 'border border-emerald-500/40 text-emerald-400' : m.status === 'quoted' ? 'border border-gold/40 text-gold' : 'bg-gold text-ink'}`}>{m.status}</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">{m.kinds.map((k) => <span key={k} className="text-sm text-cream/80 rounded-lg bg-panel2/60 border hairline px-2.5 py-1">{k}</span>)}</div>
            {m.note && <p className="text-sm text-cream/60 mb-4">&ldquo;{m.note}&rdquo;</p>}
            {m.status === 'new' ? (
              <div className="flex items-center gap-3">
                <input value={vals[m.code] || ''} onChange={(e) => setVals({ ...vals, [m.code]: e.target.value })} placeholder="Quote $" className="w-32 rounded-xl border hairline bg-panel2/50 px-4 py-2.5 text-cream outline-none focus:border-gold/50 transition-colors" />
                <button onClick={() => quote(m.code)} className="btn-gold rounded-full px-5 py-2.5 text-sm">Send quote</button>
              </div>
            ) : (
              <p className="text-sm text-cream/70">Quote: <span className="font-display text-xl text-gold">${m.quote}</span></p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Payments({ payments }) {
  return (
    <div>
      <p className="eyebrow mb-3">Studio</p>
      <h1 className="font-display text-5xl text-cream leading-[0.95]">Payments <span className="italic text-gold-grad">& receipts.</span></h1>
      <div className="mt-8 panel rounded-2xl overflow-hidden max-w-4xl">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b hairline eyebrow text-[9px]">
          <span className="col-span-3">Reference</span><span className="col-span-4">Item</span><span className="col-span-2">Date</span><span className="col-span-1">Status</span><span className="col-span-2 text-right">Amount</span>
        </div>
        {payments.map((p) => (
          <div key={p.code} className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b hairline last:border-0 items-center">
            <div className="col-span-2 md:col-span-3"><p className="text-cream text-sm">{p.business}</p><p className="text-xs text-cream/45 font-mono">{p.code}</p></div>
            <span className="hidden md:block col-span-4 text-sm text-cream/70">{p.item}</span>
            <span className="hidden md:block col-span-2 text-sm text-cream/55">{p.date}</span>
            <span className="col-span-1 md:col-span-1"><span className={`text-[10px] font-mono uppercase tracking-widest ${p.status === 'paid' ? 'text-emerald-400' : 'text-gold'}`}>{p.status}</span></span>
            <span className="col-span-1 md:col-span-2 text-right font-display text-lg text-cream">${p.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Settings() {
  const [sample, setSample] = useState(null);
  const [title, setTitle] = useState('');
  const [st, setSt] = useState({ studio_name: '', alerts_email: '', alerts_phone: '', default_preview: 90, ai_cost_cap: 40, discount_2: 10, discount_3: 15, test_buyer_emails: '', test_price: 5 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    API.sampleSong().then((s) => { setSample(s); setTitle(s.title || ''); }).catch(() => {});
    API.studioSettings().then(setSt).catch(() => {});
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const saved = await API.saveStudioSettings({ ...st, default_preview: Number(st.default_preview), ai_cost_cap: Number(st.ai_cost_cap) || 0, discount_2: Number(st.discount_2) || 0, discount_3: Number(st.discount_3) || 0, test_price: Number(st.test_price) || 5 });
      setSt(saved); toast.success('Settings saved.', { description: 'New requests will use these defaults.' });
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || 'Could not save settings.'); }
    finally { setSaving(false); }
  };

  const onUploaded = async (res, file) => {
    try {
      const saved = await API.setSampleSong({ file_id: res.id, title: title || file.name.replace(/\.[^.]+$/, '') });
      setSample(saved); setTitle(saved.title);
      toast.success('Sample song updated.', { description: 'Customers hear this on the Send it step.' });
    } catch (e) { toast.error('Could not save sample song.'); }
  };
  const saveTitle = async () => {
    if (!sample?.file_id) return;
    try { const saved = await API.setSampleSong({ file_id: sample.file_id, title }); setSample(saved); toast.success('Title saved.'); }
    catch (e) { toast.error('Could not save title.'); }
  };

  return (
    <div className="max-w-xl">
      <p className="eyebrow mb-3">Studio</p>
      <h1 className="font-display text-5xl text-cream leading-[0.95]">Settings.</h1>

      <div className="mt-8 panel rounded-2xl p-7 space-y-5" data-testid="sample-song-settings">
        <div>
          <p className="eyebrow text-[9px] mb-1">Sample song</p>
          <p className="text-sm text-cream/55">Plays on the &ldquo;Send it&rdquo; step so new customers hear your sound before they send a request.</p>
        </div>
        {sample?.url ? (
          <div className="rounded-xl border border-gold/30 bg-gold/[0.04] p-4 space-y-3">
            <AudioPlayer id="sample-song-studio" src={sample.url} compact />
            <div className="flex items-center gap-2">
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sample-title-input" placeholder="Track title" className="flex-1 rounded-xl border hairline bg-panel2/50 px-4 py-2.5 text-sm text-cream outline-none focus:border-gold/50 transition-colors" />
              <button onClick={saveTitle} data-testid="sample-title-save" className="btn-ghost rounded-full px-4 py-2.5 text-sm">Save title</button>
            </div>
          </div>
        ) : <p className="text-sm text-cream/45">No sample uploaded yet &mdash; a placeholder track plays until you add one.</p>}
        <FileDrop accept="audio/*" onUploaded={onUploaded} testId="sample-song-upload" label={sample?.url ? 'Replace sample song (MP3, WAV, M4A)' : 'Upload your sample song (MP3, WAV, M4A)'} />
      </div>

      <div className="mt-6 panel rounded-2xl p-7 space-y-5" data-testid="studio-settings-form">
        <div><label className="eyebrow text-[9px] block mb-2">Studio name</label><input value={st.studio_name} onChange={(e) => setSt({ ...st, studio_name: e.target.value })} data-testid="setting-studio-name" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /></div>
        <div><label className="eyebrow text-[9px] block mb-2">Alerts email</label><input type="email" value={st.alerts_email} onChange={(e) => setSt({ ...st, alerts_email: e.target.value })} data-testid="setting-alerts-email" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /><p className="text-xs text-cream/45 mt-2">New requests and sale alerts go here. It&rsquo;s also the reply-to on every customer email.</p></div>
        <div><label className="eyebrow text-[9px] block mb-2">Text alerts to</label><input value={st.alerts_phone} onChange={(e) => setSt({ ...st, alerts_phone: e.target.value })} data-testid="setting-alerts-phone" placeholder="+1 (555) 014-2200" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream placeholder:text-cream/35 outline-none focus:border-gold/50 transition-colors" /></div>
        <div>
          <label className="eyebrow text-[9px] block mb-2">Default customer preview</label>
          <div className="flex gap-1.5">
            {[[90, '90 sec'], [120, '120 sec'], [0, 'Full song']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setSt({ ...st, default_preview: v })} data-testid={`setting-preview-${v || 'full'}`} className={`flex-1 rounded-xl border px-2 py-3 text-xs transition-colors ${Number(st.default_preview) === v ? 'border-gold/60 text-gold bg-gold/[0.06]' : 'hairline text-cream/70 hover:border-gold/40'}`}>{l}</button>
            ))}
          </div>
        </div>
        <div><label className="eyebrow text-[9px] block mb-2">Monthly AI cost cap ($)</label><input type="number" value={st.ai_cost_cap} onChange={(e) => setSt({ ...st, ai_cost_cap: e.target.value })} data-testid="setting-ai-cap" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /></div>
        <div>
          <label className="eyebrow text-[9px] block mb-2">Multi-song discounts</label>
          <div className="grid grid-cols-2 gap-3">
            <div><input type="number" min="0" max="100" value={st.discount_2} onChange={(e) => setSt({ ...st, discount_2: e.target.value })} data-testid="setting-discount-2" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /><p className="text-xs text-cream/45 mt-2">% off the 2nd song</p></div>
            <div><input type="number" min="0" max="100" value={st.discount_3} onChange={(e) => setSt({ ...st, discount_3: e.target.value })} data-testid="setting-discount-3" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /><p className="text-xs text-cream/45 mt-2">% off the 3rd song</p></div>
          </div>
          <p className="text-xs text-cream/45 mt-2">Applies to the second and third version a customer buys, whether together or later. Versions they already own count toward the tier.</p>
        </div>
        <div className="rounded-2xl border border-gold/40 bg-gold/[0.04] p-4">
          <label className="eyebrow text-[9px] block mb-2 text-gold">Test pricing (for your own live test payments)</label>
          <input value={st.test_buyer_emails} onChange={(e) => setSt({ ...st, test_buyer_emails: e.target.value })} placeholder="you@example.com, another@example.com" data-testid="setting-test-emails" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" />
          <div className="grid grid-cols-2 gap-3 mt-3 items-end">
            <div><input type="number" min="0.5" step="0.5" value={st.test_price} onChange={(e) => setSt({ ...st, test_price: e.target.value })} data-testid="setting-test-price" className="w-full rounded-xl border hairline bg-panel2/50 px-4 py-3 text-cream outline-none focus:border-gold/50 transition-colors" /><p className="text-xs text-cream/45 mt-2">$ per line item (per month for subscriptions)</p></div>
            <p className="text-xs text-cream/60">Only customers whose email is listed here see these prices at Stripe Checkout; every line is marked "(TEST PRICE)". Everyone else pays full price. Clear the list when you're done testing.</p>
          </div>
        </div>
        <button onClick={saveSettings} disabled={saving} data-testid="settings-save-btn" className="btn-gold rounded-full px-6 py-3 flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save changes</button>
      </div>
    </div>
  );
}
